import { cn } from '@/lib/core';

/**
 * Shared wrapper so the Stripe-managed card fields sit in the same box as the project's `Input`.
 *
 * The Element itself renders in a Stripe iframe, so the border, height and padding belong to this
 * container rather than to the field — including the invalid-state border, which mirrors
 * `ui/input.tsx`'s `aria-invalid` styling.
 */
export function StripeField({
  children,
  hasIcon,
  error,
}: {
  children: React.ReactNode;
  hasIcon?: boolean;
  error?: boolean;
}) {
  return (
    <div
      className={cn(
        'bg-jncquoi-neutral-00 focus-within:border-jncquoi-neutral-1000 relative flex h-10 w-full items-center border-[0.5px] border-solid px-3.75 transition-colors',
        error ? 'border-jncquoi-asia-7427c-500' : 'border-jncquoi-neutral-200'
      )}
    >
      <div className={hasIcon ? 'min-w-0 flex-1' : 'w-full'}>{children}</div>
    </div>
  );
}

/** Which card field a Stripe error belongs to. */
export type CardField = 'number' | 'expiry' | 'cvc';

/**
 * Maps a `confirmCardSetup` / `confirmCardPayment` error onto the field that caused it.
 *
 * Stripe reports the offending parameter on card errors, which lets a decline or an
 * invalid-number response land under the right input instead of only as a form-level message.
 */
export function fieldForStripeError(param?: string, code?: string): CardField | undefined {
  if (param?.includes('number') || code === 'incorrect_number' || code === 'invalid_number') {
    return 'number';
  }
  if (param?.includes('exp') || code?.includes('expiry')) return 'expiry';
  if (param?.includes('cvc') || code?.includes('cvc')) return 'cvc';
  return undefined;
}
